import React from "react";
import { Num } from "./ui/Num";
import { Pill, PillGroup } from "./ui/Pill";
import { SEND_MODES } from "./broadcastEnums";
import { errorFor } from "./broadcastDraft";
import { toScheduledAtIso } from "./broadcastPayload";

/**
 * The bottom of the composer: when it goes out, and the button that sends it.
 *
 * Presentational only. It never estimates, never validates and never submits —
 * it renders what it is handed and calls back. Every number it shows came off
 * the wire.
 *
 * ---------------------------------------------------------------------------
 * THE NUMBERS RULE
 * ---------------------------------------------------------------------------
 * Two counts, and they are not the same count:
 *
 *   totalSends    MESSAGES. A person in both audiences is two of these.
 *   uniquePeople  HUMANS. That same person is one of these.
 *
 * Showing only `totalSends` overstates reach ("we told 26,898 people"). Showing
 * only `uniquePeople` understates cost and delivery volume. So BOTH are always
 * rendered, and the overlap (`bothChannels`) is named outright so the gap
 * between them is explained rather than left to be noticed.
 *
 * NEITHER IS EVER COMPUTED HERE. Not `push + email`, not `totalSends -
 * uniquePeople`. The server knows who is in both segments and this component
 * cannot know it — any arithmetic in this file would be a guess wearing the
 * clothes of a fact, and it would be most wrong on exactly the big
 * cross-channel sends where the number matters most.
 *
 * ---------------------------------------------------------------------------
 * A FAILED ESTIMATE IS NOT A ZERO
 * ---------------------------------------------------------------------------
 * When the estimate call fails the button reads a bare "Send" and the subline
 * says so. It must never read "Send to 0": that sentence means "this reaches
 * nobody", which is a different — and far more alarming — claim than "we don't
 * know how many". A genuine `totalSends: 0` from the server DOES render
 * "Send to 0", because that is a real answer to a real question.
 */

/** Shared button geometry. One literal so Tailwind's purger keeps it. */
const BUTTON_BASE =
  "relative inline-flex items-center justify-center rounded-lg px-4 py-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2";

const PRIMARY_BUTTON = "bg-orange-primary text-white hover:bg-[#E85A10]";

const DISABLED_BUTTON = "cursor-not-allowed opacity-50";

const INPUT =
  "rounded-lg border border-bc-border bg-white px-2.5 py-1.5 text-[13px] text-bc-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary";

const DATE_PARTS = { day: "numeric", month: "short", year: "numeric" };
const TIME_PARTS = { hour: "2-digit", minute: "2-digit", hour12: false };

/**
 * Render a `datetime-local` value as an unambiguous instant, in BOTH zones.
 *
 * e.g. "1 Sep 2026, 09:30 local · 04:00 UTC".
 *
 * This survived from the page this footer replaces and must keep surviving. A
 * `datetime-local` input has no offset in it, so "09:30" is a wall time whose
 * meaning depends entirely on whose wall it is. Half the people scheduling a
 * broadcast think in their own zone and half think in UTC because that is what
 * the API log shows them; printing only one of the two means the other half
 * schedules a send for the wrong hour and does not find out until it goes out.
 *
 * @param {string} local - The `datetime-local` value, e.g. "2026-09-01T09:30".
 * @returns {string|null} The formatted instant, or null when blank/unparseable.
 */
export const formatScheduleInstant = (local) => {
  const iso = toScheduledAtIso(local);
  if (!iso) return null;

  const when = new Date(iso);
  const date = when.toLocaleDateString(undefined, DATE_PARTS);
  const localTime = when.toLocaleTimeString(undefined, TIME_PARTS);
  const utcTime = when.toLocaleTimeString(undefined, {
    ...TIME_PARTS,
    timeZone: "UTC",
  });

  return `${date}, ${localTime} local · ${utcTime} UTC`;
};

/**
 * The schedule half: now-or-later, plus the instant that resolves to.
 *
 * @param {Object} props
 * @param {import("./broadcastDraft").BroadcastDraft} props.draft
 * @param {(patch: Object) => void} [props.onPatch]
 * @param {Array<{field: string, message: string}>} [props.errors]
 * @returns {React.ReactElement}
 */
const ScheduleControl = ({ draft, onPatch, errors }) => {
  const isLater = draft?.scheduleMode === "later";
  const scheduleError = errorFor(errors, "scheduledAtLocal");
  const instant = isLater ? formatScheduleInstant(draft?.scheduledAtLocal) : null;

  return (
    <div className="flex flex-col gap-2">
      <PillGroup label="Send time">
        {SEND_MODES.map((mode) => (
          <Pill
            key={mode.id}
            selected={draft?.scheduleMode === mode.id}
            onSelect={() => onPatch?.({ scheduleMode: mode.id })}
          >
            {mode.label}
          </Pill>
        ))}
      </PillGroup>

      {isLater && (
        <div className="flex flex-col gap-1">
          <label htmlFor="broadcast-scheduled-at" className="sr-only">
            Send at
          </label>
          <input
            id="broadcast-scheduled-at"
            type="datetime-local"
            className={INPUT}
            value={draft?.scheduledAtLocal || ""}
            onChange={(event) =>
              onPatch?.({ scheduledAtLocal: event.target.value })
            }
          />

          {scheduleError && (
            <p role="alert" className="text-xs text-bc-bad">
              {scheduleError}
            </p>
          )}

          {/* Nothing at all when the date is blank or nonsense — an
              authoritative-looking "Sends Invalid Date" is worse than silence. */}
          {instant && <p className="text-xs text-bc-soft">Sends {instant}</p>}
        </div>
      )}
    </div>
  );
};

/** Is this a count we can print? `0` is; null / undefined / NaN are not. */
const isCount = (value) => typeof value === "number" && Number.isFinite(value);

/**
 * The composer's send footer.
 *
 * @param {Object} props
 * @param {import("./broadcastDraft").BroadcastDraft} props.draft - The draft being composed.
 * @param {(patch: Object) => void} [props.onPatch] - Applies a partial draft update.
 * @param {Array<{field: string, message: string}>} [props.errors] - Validation errors.
 * @param {{totalSends: number|null, uniquePeople: number|null, bothChannels: number|null}|null} [props.estimate] - The SERVER's audience estimate. Never derived here.
 * @param {boolean} [props.estimating=false] - An estimate request is in flight.
 * @param {boolean} [props.failed=false] - The estimate could not be obtained. Suppresses every number.
 * @param {boolean} [props.canSend=true] - Whether the draft is in a sendable state.
 * @param {boolean} [props.submitting=false] - A send is in flight.
 * @param {() => void} [props.onSend] - Called when the primary button is pressed.
 * @param {string} [props.className] - Extra classes, merged last.
 * @returns {React.ReactElement}
 */
export const SendFooter = ({
  draft,
  onPatch,
  errors,
  estimate,
  estimating = false,
  failed = false,
  canSend = true,
  submitting = false,
  onSend,
  className = "",
}) => {
  const isLater = draft?.scheduleMode === "later";
  const verb = isLater ? "Schedule" : "Send";

  // A failed estimate has no numbers, whatever `estimate` happens to hold.
  const totalSends = failed ? null : estimate?.totalSends;
  const showsCount = Boolean(estimate) && !failed && isCount(totalSends);

  const uniquePeople = estimate?.uniquePeople;
  const bothChannels = estimate?.bothChannels;
  const showsBoth = !failed && isCount(bothChannels) && bothChannels > 0;

  const disabled = canSend === false || submitting;

  const label = showsCount ? (
    <>
      {isLater ? "Schedule for " : "Send to "}
      {/* `!text-white`: `Num` sets its own text colour, and on the orange
          primary that colour is unreadable. The important modifier is the only
          override that does not depend on stylesheet ordering. */}
      <Num value={totalSends} className="ml-1 !text-white" />
    </>
  ) : (
    verb
  );

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <ScheduleControl draft={draft} onPatch={onPatch} errors={errors} />

      <div className="flex flex-col items-end gap-1">
        <button
          type="button"
          disabled={disabled}
          onClick={onSend}
          className={`${BUTTON_BASE} ${PRIMARY_BUTTON} ${disabled ? DISABLED_BUTTON : ""}`}
        >
          {/* The resting label stays in the layout while submitting, hidden and
              silent, so the button cannot shrink under the cursor mid-click. */}
          <span className={submitting ? "invisible" : ""} aria-hidden={submitting}>
            {label}
          </span>
          {submitting && (
            <span className="absolute inset-0 flex items-center justify-center">
              Sending…
            </span>
          )}
        </button>

        {failed ? (
          <p className="text-xs text-bc-warn">
            Couldn&apos;t estimate the audience — you can still send.
          </p>
        ) : showsCount || isCount(uniquePeople) ? (
          <p className="text-xs text-bc-soft">
            reaching <Num value={uniquePeople} /> people
            {showsBoth && (
              <>
                {" · "}
                <Num value={bothChannels} /> get both
              </>
            )}
          </p>
        ) : estimating ? (
          <p className="text-xs text-bc-soft">Estimating…</p>
        ) : null}
      </div>
    </div>
  );
};

export default SendFooter;
